import { useCallback } from 'react';

import type { AxiosResponse } from 'axios';

import type { OrganisationAddSDBCredentials } from 'state/__generated__/tadaahAPI';
import {
  useOrganisationUpdateSDBCredentials,
  useOrganisationGetSDBCredentials,
} from 'state/__generated__/tadaahAPI';
import client from 'state/remote/client';
import useIsFreelancerLoggedIn from 'hooks/use-is-freelancer-logged-in';

interface UseSdbIntegrationHandler {
  update: {
    updateSDBCredentials: (
      values: OrganisationAddSDBCredentials,
    ) => Promise<AxiosResponse<OrganisationAddSDBCredentials, unknown>>;
    isLoading: boolean;
    error: string | undefined;
    isSuccess: boolean;
  };
  get: {
    sdbApiKey: OrganisationAddSDBCredentials['sdb_api_key'] | undefined;
    sdbAccountName:
      | OrganisationAddSDBCredentials['sdb_account_name']
      | undefined;
  };
}

interface UseSdbIntegrationParams {
  organisationId?: string;
}

const useSdbIntegration = ({
  organisationId,
}: UseSdbIntegrationParams): UseSdbIntegrationHandler => {
  const isLoggedIn = useIsFreelancerLoggedIn();

  const { data } = useOrganisationGetSDBCredentials(organisationId as string, {
    query: {
      enabled: isLoggedIn && !!organisationId,
      queryKey: ['get-organisation-sdb-credentials', organisationId],
    },
  });

  const { mutateAsync, isLoading, error, isSuccess } =
    useOrganisationUpdateSDBCredentials({
      mutation: {
        mutationKey: 'update-organisation-sdb-credentials',
        onSuccess: () => {
          const queriesToInvalidate = [
            'organisation-admin-details',
            'get-organisation-sdb-credentials',
          ];
          queriesToInvalidate.forEach((query) => {
            client.invalidateQueries(query);
          });
        },
      },
    });

  const updateSDBCredentials = useCallback(
    async (values: OrganisationAddSDBCredentials) => {
      if (!organisationId) throw new Error('No organisation selected');

      const response = await mutateAsync({
        data: values,
        id: organisationId.toString(),
      });

      return response;
    },
    [mutateAsync, organisationId],
  );
  return {
    get: {
      sdbAccountName: data?.data.sdb_account_name as unknown as
        | OrganisationAddSDBCredentials['sdb_account_name']
        | undefined,
      sdbApiKey: data?.data.sdb_api_key as unknown as
        | OrganisationAddSDBCredentials['sdb_api_key']
        | undefined,
    },
    update: {
      error: error?.response?.data
        ? (error?.response?.data as unknown as { detail: string })?.detail
        : error?.message,
      isLoading,
      isSuccess,
      updateSDBCredentials,
    },
  };
};

export default useSdbIntegration;
